/**
 * product-compare-toggle.js
 *
 * Wires <product-compare-toggle> elements to the Spec & Compare app and enforces
 * that only products from the same comparator family can be in the compare list.
 * Data comes from the JSON script (comparator-families-data snippet) and the
 * <sc-dynamic-compare> instance.
 */
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const COMPARE_INSTANCE_SELECTOR = 'sc-dynamic-compare';
const EVENT_COMPARE_REMOVE = 'sc:dynamic-compare:remove';
const FAMILIES_DATA_SCRIPT_ID = 'product-compare-families-data';

// -----------------------------------------------------------------------------
// State
// -----------------------------------------------------------------------------

/**
 * @typedef {{
 *  id: string;
 *  handle: string;
 *  family_name: string;
 *  category: string;
 *  range: string;
 *  product_handles: string[];
 * }} Family
 */

/** Map of product handle -> array of family ids. */
const familiesByProductHandle = new Map();

/** Active family id once locked (after at least two compatible products selected). @type {string | null} */
let activeFamilyId = null;

/** Candidate family ids when only the first product has been selected. @type {Set<string> | null} */
let candidateFamilyIds = null;

/** Registered toggle elements on the page. */
const toggles = new Set();

let familiesInitialized = false;

/**
 * @typedef {{ button?: HTMLButtonElement }} ProductCompareToggleRefs
 */

// -----------------------------------------------------------------------------
// Custom element
// -----------------------------------------------------------------------------

/**
 * Custom element that controls adding/removing products from the
 * Spec & Compare dynamic comparison list while enforcing same-family comparison.
 *
 * @extends {Component<ProductCompareToggleRefs>}
 */
class ProductCompareToggle extends Component {
  /** @type {string[]} */
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();

    this.button = this.querySelector('.product-compare-toggle__button') ?? undefined;
    this.productHandle = this.dataset.productHandle ?? '';

    if (!this.productHandle || !(this.button instanceof HTMLButtonElement)) return;

    this.button.addEventListener('click', this.#onClick);
    registerToggle(this);

    // Initial sync with dynamic compare instance if available
    syncToggleSelectionFromInstance(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.button instanceof HTMLButtonElement) {
      this.button.removeEventListener('click', this.#onClick);
    }
    unregisterToggle(this);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (this.dataset.disabled === 'true' || this.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    handleToggleClick(this);
  };
}

if (!customElements.get('product-compare-toggle')) {
  customElements.define('product-compare-toggle', ProductCompareToggle);
}

// -----------------------------------------------------------------------------
// Family data
// -----------------------------------------------------------------------------

/**
 * Initializes family data from the JSON script if not already done.
 */
function ensureFamiliesLoaded() {
  if (familiesInitialized) return;
  familiesInitialized = true;

  const script = /** @type {HTMLScriptElement | null} */ (
    document.getElementById(FAMILIES_DATA_SCRIPT_ID)
  );
  if (!script) return;

  try {
    /** @type {Family[]} */
    const data = JSON.parse(script.textContent || '[]');

    for (const family of data) {
      const familyId = String(family.id);
      const handles = family.product_handles ?? [];
      for (const handle of handles) {
        const normalizedHandle = String(handle);
        const existing = familiesByProductHandle.get(normalizedHandle) || [];
        existing.push(familyId);
        familiesByProductHandle.set(normalizedHandle, existing);
      }
    }
  } catch {
    // Parsing failed; family data will be empty.
  }
}

/**
 * Registers a toggle instance for later re-evaluation.
 * @param {ProductCompareToggle} toggle
 */
function registerToggle(toggle) {
  ensureFamiliesLoaded();
  toggles.add(toggle);
}

/**
 * Unregisters a toggle instance.
 * @param {ProductCompareToggle} toggle
 */
function unregisterToggle(toggle) {
  toggles.delete(toggle);
}

// -----------------------------------------------------------------------------
// Events
// -----------------------------------------------------------------------------

/**
 * Resets compare state when collection filters are applied so the next
 * product selection is treated as the first (no stale candidate/active family).
 * Keeps our state in sync with the facets clearing the app's compare list.
 */
function resetCompareStateOnFilterUpdate() {
  activeFamilyId = null;
  candidateFamilyIds = null;

  for (const toggle of toggles) {
    toggle.dataset.selected = 'false';
    setToggleDisabled(toggle, false);
  }
}

document.addEventListener(ThemeEvents.FilterUpdate, resetCompareStateOnFilterUpdate);

/**
 * Clears the Spec & Compare compare list (if supported by the app instance).
 */
function clearDynamicCompareList() {
  const instance = getDynamicCompareInstance();
  if (!instance) return;

  try {
    if (typeof instance.clearProducts === 'function') {
      instance.clearProducts();
    }
  } catch {
    // Ignore if app instance isn't ready.
  }
}

/**
 * When pagination updates the collection grid, reset the comparator so the UI
 * matches the new grid content (same behavior as filter updates).
 */
function resetCompareStateOnPaginationUpdate() {
  clearDynamicCompareList();
  resetCompareStateOnFilterUpdate();
}

document.addEventListener(ThemeEvents.paginatedListUpdate, resetCompareStateOnPaginationUpdate);

/**
 * Handles removal of a product from the compare list (e.g. via the modal).
 * Syncs toggle UI and family state so cards and availability stay correct.
 * @param {CustomEvent} event expects event.detail.handle or event.detail.productHandle
 */
function handleCompareRemove(event) {
  const instance = getDynamicCompareInstance();
  if (!instance) return;

  const removedHandle =
    event.detail?.handle ?? event.detail?.productHandle ?? null;

  if (removedHandle) {
    for (const toggle of toggles) {
      if (toggle.dataset.productHandle === removedHandle) {
        toggle.dataset.selected = 'false';
        break;
      }
    }
  } else {
    for (const toggle of toggles) {
      syncToggleSelectionFromInstance(toggle);
    }
  }

  syncFamilyStateFromCompareList(instance);
  reevaluateToggleAvailability();
}

document.addEventListener(EVENT_COMPARE_REMOVE, handleCompareRemove);

// -----------------------------------------------------------------------------
// Compare instance
// -----------------------------------------------------------------------------

/**
 * @typedef {HTMLElement & {
 *   containsProduct?: (handle: string) => boolean;
 *   removeProduct?: (handle: string) => void;
 *   toggleProduct?: (handle: string) => void;
 *   addProduct?: (handle: string) => void;
 *   clearProducts?: () => void;
 *   getCount?: () => number;
 *   getHandles?: () => string[];
 * }} CompareInstance
 */

/**
 * @returns {CompareInstance | null}
 */
function getDynamicCompareInstance() {
  return document.querySelector(COMPARE_INSTANCE_SELECTOR);
}

/**
 * Syncs a single toggle's selected state from the Spec & Compare instance.
 * @param {ProductCompareToggle} toggle
 */
function syncToggleSelectionFromInstance(toggle) {
  const instance = getDynamicCompareInstance();
  if (!instance || !toggle.dataset.productHandle) return;

  try {
    const isSelected = typeof instance.containsProduct === 'function'
      ? !!instance.containsProduct(toggle.dataset.productHandle)
      : false;

    toggle.dataset.selected = isSelected ? 'true' : 'false';
  } catch {
    // If the app instance isn't ready yet, silently ignore.
  }
}

// -----------------------------------------------------------------------------
// Click handling
// -----------------------------------------------------------------------------

/**
 * Removes the toggle's product from the compare list and refreshes family state.
 * @param {CompareInstance} instance
 * @param {ProductCompareToggle} toggle
 */
function removeFromCompare(instance, toggle) {
  const handle = toggle.dataset.productHandle;
  if (!handle) return;

  if (typeof instance.removeProduct === 'function') {
    instance.removeProduct(handle);
  } else if (typeof instance.toggleProduct === 'function') {
    instance.toggleProduct(handle);
  }

  toggle.dataset.selected = 'false';
  syncFamilyStateFromCompareList(instance);
  reevaluateToggleAvailability();
}

/**
 * Updates family state for adding a product. Mutates activeFamilyId/candidateFamilyIds
 * when the product is allowed. Returns whether the add is allowed.
 * @param {Set<string>} productFamilies family ids for the product being added
 * @returns {{ allowed: boolean }}
 */
function applyFamilyStateForAdd(productFamilies) {
  if (!activeFamilyId && !candidateFamilyIds) {
    candidateFamilyIds = new Set(productFamilies);
    return { allowed: true };
  }

  if (!activeFamilyId && candidateFamilyIds) {
    const intersection = new Set(
      [...candidateFamilyIds].filter((id) => productFamilies.has(id))
    );
    if (!intersection.size) {
      return { allowed: false };
    }
    if (intersection.size === 1) {
      const single = intersection.values().next().value;
      activeFamilyId = single != null ? String(single) : null;
      candidateFamilyIds = null;
    } else {
      candidateFamilyIds = intersection;
    }
    return { allowed: true };
  }

  if (activeFamilyId) {
    return { allowed: productFamilies.has(activeFamilyId) };
  }

  return { allowed: true };
}

/**
 * Handles a click on a toggle: remove if already selected, or add with family gating.
 * @param {ProductCompareToggle} toggle
 */
function handleToggleClick(toggle) {
  ensureFamiliesLoaded();

  const instance = getDynamicCompareInstance();
  const handle = toggle.dataset.productHandle;

  if (!instance || !handle) return;

  const productFamilies = new Set(familiesByProductHandle.get(handle) || []);
  if (!productFamilies.size) {
    return;
  }

  const isCurrentlySelected =
    toggle.dataset.selected === 'true' ||
    (typeof instance.containsProduct === 'function' && !!instance.containsProduct(handle));

  if (isCurrentlySelected) {
    removeFromCompare(instance, toggle);
    return;
  }

  const { allowed } = applyFamilyStateForAdd(productFamilies);
  if (!allowed) {
    setToggleDisabled(toggle, true);
    return;
  }

  try {
    if (typeof instance.toggleProduct === 'function') {
      instance.toggleProduct(handle);
    } else if (typeof instance.addProduct === 'function') {
      instance.addProduct(handle);
    }
    toggle.dataset.selected = 'true';
  } catch {
    // App instance failed to update; leave toggle state unchanged.
  }

  reevaluateToggleAvailability();
}

// -----------------------------------------------------------------------------
// Toggle UI
// -----------------------------------------------------------------------------

/**
 * Sets or clears the disabled state of a toggle (dataset, aria, class, button, card).
 * @param {ProductCompareToggle} toggle
 * @param {boolean} disabled
 */
function setToggleDisabled(toggle, disabled) {
  if (disabled) {
    toggle.dataset.disabled = 'true';
    toggle.setAttribute('aria-disabled', 'true');
    toggle.classList.add('product-compare-toggle--disabled');
  } else {
    delete toggle.dataset.disabled;
    toggle.setAttribute('aria-disabled', 'false');
    toggle.classList.remove('product-compare-toggle--disabled');
  }

  const button = toggle.querySelector('.product-compare-toggle__button');
  if (button instanceof HTMLButtonElement) {
    button.disabled = disabled;
  }

  const card = toggle.closest('product-card');
  if (card instanceof HTMLElement) {
    if (disabled) {
      card.classList.add('product-card--compare-disabled');
    } else {
      card.classList.remove('product-card--compare-disabled');
    }
  }
}

// -----------------------------------------------------------------------------
// Family state
// -----------------------------------------------------------------------------

/**
 * Returns the number of products currently in the compare list.
 * @param {CompareInstance} instance
 * @returns {number}
 */
function getCompareListCount(instance) {
  try {
    if (typeof instance.getCount === 'function') {
      return Number(instance.getCount() || 0);
    }
    if (Array.isArray(instance.getHandles?.())) {
      return instance.getHandles().length;
    }
  } catch {
    // Fall through to default.
  }
  return 0;
}

/**
 * Syncs family state with the current compare list: resets when empty,
 * otherwise recomputes active/candidate families from the remaining selection.
 * @param {CompareInstance} instance
 */
function syncFamilyStateFromCompareList(instance) {
  try {
    const count = getCompareListCount(instance);

    if (count === 0) {
      activeFamilyId = null;
      candidateFamilyIds = null;

      for (const toggle of toggles) {
        setToggleDisabled(toggle, false);
        syncToggleSelectionFromInstance(toggle);
      }

      return;
    }
  } catch {
    // If we can't read the state, fall through to recompute from toggles.
  }

  // When there are still compared products left, recompute the active / candidate
  // families from the currently selected toggles instead of keeping stale state.
  recomputeFamiliesFromSelection();
}

/**
 * Re-evaluates which toggles should be disabled based on the current
 * active family or candidate families.
 */
function reevaluateToggleAvailability() {
  ensureFamiliesLoaded();

  const isFamilyGatingActive = Boolean(activeFamilyId || candidateFamilyIds);

  for (const toggle of toggles) {
    const handle = toggle.dataset.productHandle;
    if (!handle) continue;

    const familyIds = new Set(familiesByProductHandle.get(handle) || []);

    // Always start from a clean state.
    setToggleDisabled(toggle, false);

    if (!familyIds.size) {
      // If this product isn't present in the families JSON, only disable it
      // while we are actively enforcing a compare family. When compare is empty
      // (no gating), keep it enabled.
      if (isFamilyGatingActive) {
        setToggleDisabled(toggle, true);
      }
      continue;
    }

    if (activeFamilyId) {
      if (!familyIds.has(activeFamilyId)) {
        setToggleDisabled(toggle, true);
      }
      continue;
    }

    if (candidateFamilyIds) {
      const hasOverlap = [...candidateFamilyIds].some((id) => familyIds.has(id));
      if (!hasOverlap) {
        setToggleDisabled(toggle, true);
      }
    }
  }
}

/**
 * Recomputes activeFamilyId and candidateFamilyIds purely from the set
 * of currently selected toggles. This is used after removals so that,
 * for example, if only a multi-family product remains selected, we go
 * back to having multiple candidate families instead of a locked one.
 */
function recomputeFamiliesFromSelection() {
  const selectedToggles = [...toggles].filter((toggle) => toggle.dataset.selected === 'true');

  if (!selectedToggles.length) {
    activeFamilyId = null;
    candidateFamilyIds = null;
    return;
  }

  const firstToggle = selectedToggles[0];
  if (!firstToggle) return;

  const firstHandle = firstToggle.dataset.productHandle ?? '';
  const firstFamilies = new Set(familiesByProductHandle.get(firstHandle) || []);

  let intersection = new Set(firstFamilies);

  for (let i = 1; i < selectedToggles.length; i += 1) {
    const toggle = selectedToggles[i];
    if (!toggle) continue;
    const handle = toggle.dataset.productHandle ?? '';
    const families = new Set(familiesByProductHandle.get(handle) || []);

    intersection = new Set([...intersection].filter((id) => families.has(id)));
  }

  if (!intersection.size) {
    // This shouldn't happen given our gating logic, but if it does,
    // fall back to the first product's families as candidates.
    candidateFamilyIds = firstFamilies;
    activeFamilyId = null;
    return;
  }

  if (intersection.size === 1) {
    const single = intersection.values().next().value;
    activeFamilyId = single != null ? String(single) : null;
    candidateFamilyIds = null;
  } else {
    activeFamilyId = null;
    candidateFamilyIds = intersection;
  }
}

